"use client";

import React, { ReactNode, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  className?: string;
  position?: "center" | "left" | "right" | "top" | "bottom"; // Kept for backwards compatibility
}

export function Tooltip({ children, content, className = "" }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: -9999, left: -9999 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top = triggerRect.top - tooltipRect.height - 8;
    let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

    // Prevent clipping on left/right viewport edges
    if (left < 16) left = 16;
    if (left + tooltipRect.width > window.innerWidth - 16) {
      left = window.innerWidth - tooltipRect.width - 16;
    }

    // Default 'top' position, adjust to bottom if it falls off top edge
    if (top < 16) {
      top = triggerRect.bottom + 8;
    }

    setCoords({ top, left });
  };

  useEffect(() => {
    if (isVisible) {
      // Wait for layout to calculate dimensions
      requestAnimationFrame(updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    } else {
      setCoords({ top: -9999, left: -9999 });
    }
  }, [isVisible]);

  return (
    <div
      ref={triggerRef}
      className={`relative inline-flex items-center gap-1.5 w-fit cursor-help group ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />

      {isVisible &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[100] w-max max-w-[280px] p-2.5 bg-card text-foreground text-xs rounded-lg shadow-xl border border-border/60 font-sans normal-case tracking-normal animate-in fade-in-0 zoom-in-95 pointer-events-none"
            style={{
              top: coords.top,
              left: coords.left,
              visibility: coords.top === -9999 ? "hidden" : "visible",
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </div>
  );
}
